import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import html2canvas from 'html2canvas';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import { LoadingSpinner } from '../../core/LoadingSpinner';
import BrushIcon from '@mui/icons-material/Brush';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined';
import { CustomeTextField } from '../../generic/CustomeTextField';


pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();


interface PaymentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    file: File | null,
    meta: { title: string; amount: number }
  ) => void | Promise<void>;
  initialTitle?: string;
  initialAmount?: number;
  requireFile?: boolean;
  lockMetaFields?: boolean;
  dialogTitle?: string;
  closeOnSuccess?: boolean;
}

const PaymentUploadDialog: React.FC<PaymentUploadDialogProps> = ({
  open,
  onClose,
  onSave,
  initialTitle = '',
  initialAmount,
  requireFile = true,
  lockMetaFields = false,
  dialogTitle = 'Neue Zahlung hinzufügen',
  closeOnSuccess = true,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [amount, setAmount] = useState<number | ''>(initialAmount ?? '');
  const [file, setFile] = useState<File | null>(null);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const pdfUrl = useMemo(() => {
    if (!pdfData) return null;
    const blob = new Blob([pdfData], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }, [pdfData]);

  const [isExporting, setIsExporting] = useState(false);

  // PDF Navigation
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Zeichnen
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(18);
  const [drawEnabled, setDrawEnabled] = useState(false); // Pinsel an/aus

  // Overlays
  const [pageOverlays, setPageOverlays] = useState<
    Record<number, string | null>
  >({});
  const overlaysRef = useRef<Record<number, string | null>>({});

  //Fixe Höhe (Ging immer hoch und runter)
  const [fixedPreviewHeight, setFixedPreviewHeight] = useState<number | null>(
    null
  );

  //Radierer
  type Tool = 'brush' | 'eraser';
  const [tool, setTool] = useState<Tool>('brush');

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setAmount(initialAmount ?? '');
      setFile(null);
      setImageUrl(null);
      setPdfData(null);
      setNumPages(0);
      setCurrentPage(1);
      setPageOverlays({});
      overlaysRef.current = {};
      clearOverlay();
      setDrawEnabled(false);
      setIsExporting(false);
      setFixedPreviewHeight(null);
      //Test merken
      setTool('brush');
    }
  }, [open, initialTitle, initialAmount]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [pdfUrl, imageUrl]);

  // ======= Datei laden =======
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setFile(f);
    setCurrentPage(1);
    setPageOverlays({});
    overlaysRef.current = {};
    clearOverlay();
    setDrawEnabled(false);
    setFixedPreviewHeight(null);

    if (f.type === 'application/pdf') {
      const buf = await f.arrayBuffer();
      setPdfData(new Uint8Array(buf));
      setImageUrl(null);
    } else {
      setPdfData(null);
      setImageUrl(URL.createObjectURL(f));
    }
  };

  const captureImageWithOverlayToBlob = async (): Promise<Blob> => {
    const imgEl = containerRef.current?.querySelector(
      'img'
    ) as HTMLImageElement | null;
    const overlay = overlayRef.current;
    if (!imgEl || !overlay) throw new Error('image/overlay not found');

    // Sicherstellen, dass das Bild geladen ist
    if (!imgEl.complete || imgEl.naturalWidth === 0) {
      await new Promise<void>((resolve, reject) => {
        imgEl.onload = () => resolve();
        imgEl.onerror = () => reject(new Error('image load failed'));
      });
    }

    // Offscreen-Canvas in Bild-Originalgröße
    const out = document.createElement('canvas');
    out.width = imgEl.naturalWidth;
    out.height = imgEl.naturalHeight;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('no 2d ctx');

    // 1) Originalbild zeichnen
    ctx.drawImage(imgEl, 0, 0, out.width, out.height);

    // 2) Overlay
    const overlayUrl = overlay.toDataURL('image/png');
    await new Promise<void>(resolve => {
      const ovImg = new Image();
      (ovImg as any).decoding = 'sync';
      ovImg.onload = () => {
        ctx.drawImage(ovImg, 0, 0, out.width, out.height);
        resolve();
      };
      ovImg.onerror = () => resolve();
      ovImg.src = overlayUrl;
    });

    return await new Promise<Blob>((resolve, reject) => {
      out.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/png'
      );
    });
  };

  // ======= Größen/Position sync =======
  const getContentElement = (): HTMLCanvasElement | HTMLImageElement | null => {
    if (!containerRef.current) return null;
    const pdfCanvas = containerRef.current.querySelector(
      'canvas:not([data-overlay="true"])'
    );
    if (pdfCanvas) return pdfCanvas as HTMLCanvasElement;
    const img = containerRef.current.querySelector('img');
    if (img) return img as HTMLImageElement;
    return null;
  };

  const syncOverlayToContent = () => {
    const content = getContentElement();
    const overlay = overlayRef.current;
    const container = containerRef.current;
    if (!content || !overlay || !container) return;

    const contentRect = content.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const width = Math.max(1, Math.floor(contentRect.width));
    const height = Math.max(1, Math.floor(contentRect.height));
    overlay.width = width;
    overlay.height = height;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;

    const left = contentRect.left - containerRect.left;
    const top = contentRect.top - containerRect.top;
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
  };

  const clearOverlay = () => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, overlay.width, overlay.height);
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      syncOverlayToContent();
    });
    return () => cancelAnimationFrame(raf);
  }, [pdfUrl, imageUrl, currentPage]);

  useEffect(() => {
    const onResize = () => {
      syncOverlayToContent();
      setFixedPreviewHeight(null);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ======= Cursor für Pinsel =======
  const makeBrushCursor = (size: number, tool: Tool) => {
    const r = Math.max(2, Math.round(size / 2));
    const isEraser = tool === 'eraser';
    const fill = isEraser ? 'white' : 'black';
    const opacity = isEraser ? 0.6 : 1.0;
    const stroke = 'gray';
    const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>
      <circle 
        cx='${r}' 
        cy='${r}' 
        r='${r - 1}' 
        fill='${fill}' 
        fill-opacity='${opacity}' 
        stroke='${stroke}' 
        stroke-width='1' 
      />
    </svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${r} ${r}, crosshair`;
  };

  useEffect(() => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    canvas.style.cursor = drawEnabled
      ? makeBrushCursor(brushSize, tool)
      : 'default';
  }, [drawEnabled, brushSize, tool]);

  // ======= Overlay je Seite sichern/wiederherstellen =======
  const saveOverlayForPage = (page: number): string | null => {
    const canvas = overlayRef.current;
    if (!canvas) return null;
    try {
      const url = canvas.toDataURL('image/png');
      overlaysRef.current = { ...overlaysRef.current, [page]: url };
      setPageOverlays(prev => ({ ...prev, [page]: url }));
      return url;
    } catch {
      return null;
    }
  };

  const restoreOverlayForPage = (page: number): Promise<void> => {
    return new Promise<void>(resolve => {
      const url = overlaysRef.current[page];
      const canvas = overlayRef.current;
      if (!canvas) return resolve();
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve();

      clearOverlay();
      if (!url) return resolve();

      const img = new Image();
      (img as any).decoding = 'sync';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve();
      };
      img.onerror = () => resolve();
      img.src = url;
    });
  };

  const renderedResolverRef = useRef<(() => void) | null>(null);
  const waitForRendered = () =>
    new Promise<void>(resolve => {
      renderedResolverRef.current = resolve;
    });

  const handleRendered = () => {
    requestAnimationFrame(() => {
      syncOverlayToContent();

      if (fixedPreviewHeight == null) {
        const content = getContentElement();
        if (content) {
          const h = Math.ceil(content.getBoundingClientRect().height);
          if (h > 0) {
            setFixedPreviewHeight(h);
          }
        }
      }

      if (renderedResolverRef.current) {
        renderedResolverRef.current();
        renderedResolverRef.current = null;
      }
    });
  };

  // ======= Seitenwechsel =======
  const goToPage = async (target: number) => {
    saveOverlayForPage(currentPage);
    clearOverlay();
    setCurrentPage(target);
    await waitForRendered();
    await restoreOverlayForPage(target);
    await new Promise(r => requestAnimationFrame(() => r(null)));
  };

  // ======= Zeichnen =======
  const getScaledPos = (clientX: number, clientY: number) => {
    const canvas = overlayRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawMouse = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawEnabled || isExporting) return;
    e.preventDefault();
    const { x, y } = getScaledPos(e.clientX, e.clientY);
    lastPointRef.current = { x, y };
    setIsDrawing(true);
  };
  const moveDrawMouse = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawEnabled || isExporting) return;
    if (!isDrawing) return;
    const { x, y } = getScaledPos(e.clientX, e.clientY);
    drawLineTo(x, y);
  };
  const startDrawTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!drawEnabled || isExporting) return;
    e.preventDefault();
    const t = e.touches[0];
    const { x, y } = getScaledPos(t.clientX, t.clientY);
    lastPointRef.current = { x, y };
    setIsDrawing(true);
  };
  const moveDrawTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!drawEnabled || isExporting) return;
    if (!isDrawing) return;
    const t = e.touches[0];
    const { x, y } = getScaledPos(t.clientX, t.clientY);
    drawLineTo(x, y);
  };
  const endDraw = () => {
    if (!drawEnabled || isExporting) return;
    setIsDrawing(false);
    lastPointRef.current = null;
  };

  const drawLineTo = (x: number, y: number) => {
    const canvas = overlayRef.current;
    const last = lastPointRef.current;
    if (!canvas || !last) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.globalCompositeOperation =
      tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = '#000'; // Farbe nur relevant für Brush
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();

    lastPointRef.current = { x, y };
  };

  // ======= Export-Helper =======
  const captureContainerToBlob = async (): Promise<Blob> => {
    const container = document.getElementById('preview-container');
    if (!container) throw new Error('container not found');
    const canvas = await html2canvas(container, {
      useCORS: true,
      scale: 2,
      backgroundColor: '#fff',
    });
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        b => (b ? resolve(b) : reject(new Error('toBlob failed'))),
        'image/png'
      );
    });
  };

  const buildPdfFromPngBlobs = async (blobs: Blob[]): Promise<Uint8Array> => {
    const pdfDoc = await PDFDocument.create();
    for (const blob of blobs) {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const png = await pdfDoc.embedPng(bytes);
      const page = pdfDoc.addPage([png.width, png.height]);
      page.drawImage(png, { x: 0, y: 0, width: png.width, height: png.height });
    }
    return await pdfDoc.save();
  };

  // ======= Seite p sicher rendern (für Export) =======
  const ensurePageRendered = async (p: number) => {
    if (currentPage !== p) {
      setCurrentPage(p);
      await waitForRendered();
    } else {
      await new Promise(r =>
        requestAnimationFrame(() => {
          syncOverlayToContent();
          r(null);
        })
      );
    }
    await restoreOverlayForPage(p);
    await new Promise(r => requestAnimationFrame(() => r(null))); // 1 Frame Puffer
  };

  // ======= Speichern =======
  const handleSave = async () => {
    if (requireFile && !file) return;

    let outFile: File | null = null;
    setIsExporting(true);

    try {
      if (file) {
        saveOverlayForPage(currentPage);
      }

      const pngBlobs: Blob[] = [];

      if (file && pdfUrl && numPages > 0) {
        for (let p = 1; p <= numPages; p++) {
          await ensurePageRendered(p);
          const blob = await captureContainerToBlob();
          pngBlobs.push(blob);
        }
      } else if (file && imageUrl) {
        const blob = await captureImageWithOverlayToBlob();
        pngBlobs.push(blob);
      }

      if (file) {
        const finalPdfBytes = await buildPdfFromPngBlobs(pngBlobs);
        const base = file.name.replace(/\.[^/.]+$/, '');
        const outName = `geschwärzt_${base}.pdf`;
        outFile = new File([finalPdfBytes], outName, {
          type: 'application/pdf',
        });
      }

      const maybePromise = onSave(outFile, { title, amount: Number(amount) });
      if (
        maybePromise &&
        typeof (maybePromise as Promise<void>).then === 'function'
      ) {
        await (maybePromise as Promise<void>);
      }

      if (closeOnSuccess) {
        onClose();
      }
    } catch (err) {
      console.error('[PaymentUploadDialog] save failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isExporting ? undefined : onClose}
      maxWidth={pdfUrl || imageUrl ? 'md' : 'sm'}
      fullWidth
    >
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent sx={{ position: 'relative' }}>
        {isExporting && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <LoadingSpinner />
          </Box>
        )}

        {/* Formularfelder */}
        <Stack
          direction={
            pdfUrl || imageUrl ? { xs: 'column', sm: 'row' } : 'column'
          }
          spacing={2}
          sx={{ mt: 1, mb: 2 }}
        >
          <CustomeTextField
            label='Titel'
            type='string'
            attr='title'
            object={{ title }}
            setObject={(o: { title: string }) => setTitle(o.title)}
            disabled={isExporting || lockMetaFields}
            size='small'
          />

          <CustomeTextField
            label='Betrag'
            type='euro'
            attr='amount'
            object={{ amount }}
            setObject={(o: { amount: number }) => setAmount(o.amount)}
            disabled={isExporting || lockMetaFields}
            size='small'
          />
        </Stack>

        {/* Upload-Button bei noch keiner Datei */}
        {!pdfUrl && !imageUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button variant='outlined' component='label' disabled={isExporting}>
              Datei hochladen
              <input
                type='file'
                accept='image/png, image/jpeg, image/jpg, application/pdf'
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>
        )}

        {/* Preview */}
        {(pdfUrl || imageUrl) && (
          <>
            {/* Toolbar-Bereich */}
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                width: '100%',
                flexWrap: 'wrap',
                textAlign: 'center',
              }}
            >
              {pdfUrl && numPages > 1 && (
                <>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      const target = Math.max(1, currentPage - 1);
                      void goToPage(target);
                    }}
                    disabled={isExporting || currentPage <= 1}
                  >
                    Zurück
                  </Button>
                  <Typography variant='body2'>
                    Seite {currentPage} / {numPages}
                  </Typography>
                  <Button
                    variant='outlined'
                    onClick={() => {
                      const target = Math.min(numPages, currentPage + 1);
                      void goToPage(target);
                    }}
                    disabled={isExporting || currentPage >= numPages}
                  >
                    Weiter
                  </Button>
                </>
              )}

              {/* Zeichnen an/aus */}

              <Tooltip
                title={
                  drawEnabled && tool === 'brush'
                    ? 'Zeichnen deaktivieren'
                    : 'Pinsel aktivieren'
                }
              >
                <span>
                  <IconButton
                    aria-pressed={drawEnabled && tool === 'brush'}
                    onClick={() => {
                      setTool('brush');
                      setDrawEnabled(v => (tool === 'brush' ? !v : true)); // aktiver Tool-Button toggelt, sonst aktiviert
                    }}
                    disabled={isExporting}
                    color={
                      drawEnabled && tool === 'brush' ? 'primary' : 'default'
                    }
                    sx={{
                      border: theme =>
                        `1px solid ${drawEnabled && tool === 'brush' ? theme.palette.primary.main : theme.palette.divider}`,
                      bgcolor:
                        drawEnabled && tool === 'brush'
                          ? 'action.hover'
                          : 'transparent',
                    }}
                  >
                    {drawEnabled && tool === 'brush' ? (
                      <BrushIcon />
                    ) : (
                      <BrushOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  drawEnabled && tool === 'eraser'
                    ? 'Radierer deaktivieren'
                    : 'Radierer aktivieren'
                }
              >
                <span>
                  <IconButton
                    aria-pressed={drawEnabled && tool === 'eraser'}
                    onClick={() => {
                      setTool('eraser');
                      setDrawEnabled(v => (tool === 'eraser' ? !v : true));
                    }}
                    disabled={isExporting}
                    color={
                      drawEnabled && tool === 'eraser' ? 'primary' : 'default'
                    }
                    sx={{
                      border: theme =>
                        `1px solid ${drawEnabled && tool === 'eraser' ? theme.palette.primary.main : theme.palette.divider}`,
                      bgcolor:
                        drawEnabled && tool === 'eraser'
                          ? 'action.hover'
                          : 'transparent',
                    }}
                  >
                    {drawEnabled && tool === 'eraser' ? (
                      <CleaningServicesIcon />
                    ) : (
                      <CleaningServicesOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant='body2' sx={{ minWidth: 80 }}>
                Pinselgröße
              </Typography>

              <Slider
                min={6}
                max={48}
                step={2}
                value={brushSize}
                onChange={(_, v) => setBrushSize(v as number)}
                sx={{ flex: 1, maxWidth: 300 }}
                disabled={isExporting || !drawEnabled}
              />

              <Button onClick={clearOverlay} disabled={isExporting}>
                Seite leeren
              </Button>
            </Box>

            <Box
              id='preview-container'
              ref={containerRef}
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                border: '1px solid #ccc',
                backgroundColor: '#fff',
                userSelect: 'none',
                width: '100%',
                height: fixedPreviewHeight ?? 480,
                overflow: 'hidden',
              }}
            >
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={(err) => console.error('PDF load error:', err)}
                >
                  <Page
                    pageNumber={currentPage}
                    width={containerRef.current?.clientWidth || undefined}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onRenderSuccess={handleRendered}
                  />
                </Document>
              ) : (
                <img
                  src={imageUrl!}
                  alt='Vorschau'
                  style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                  onLoad={() => requestAnimationFrame(handleRendered)}
                />
              )}

              <canvas
                ref={overlayRef}
                data-overlay='true'
                style={{ position: 'absolute', touchAction: 'none' }}
                onMouseDown={startDrawMouse}
                onMouseMove={moveDrawMouse}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDrawTouch}
                onTouchMove={moveDrawTouch}
                onTouchEnd={endDraw}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={
            isExporting ||
            !title ||
            amount === '' ||
            Number.isNaN(Number(amount)) ||
            (requireFile && !file)
          }
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentUploadDialog;
